/**
 * Records (08 §2.10, 04 §5): the missions flown, with attempts, completions, best score and fastest success;
 * the longest-serving and most decorated rangers; the best streak; operations won and rangers lost. All of it
 * is derived by `GameApi.records` - nothing here is kept.
 *
 * Thirty-six missions are too many to list as rows of dashes, so the table shows only what has been
 * ATTEMPTED - the 1987 twelve first, then the expansion by category, as the Practice picker groups them -
 * and counts the rest. It scrolls; the body takes the focus so Page Up / Page Down work from the keyboard.
 */

import './tables.css';
import { mmss } from '../hud/dom';
import type { ArchetypeRecord, Records } from '../../meta/types';
import { RANKS } from '../../meta/types';
import type { ArchetypeInfo, MissionGroup } from './archetypes';
import { archetypeNumber, groupedArchetypes } from './archetypes';
import type { Ctx, Screen } from './context';
import { box, button, el, errorMessage, panel } from './dom';
import type { Standings } from './wallPort';
import { frame } from './widgets/frame';
import type { KeyHint } from './widgets/keyHints';
import { NAV_HINTS } from './widgets/keyHints';

const HINTS: readonly KeyHint[] = [...NAV_HINTS, { keys: 'PGUP/PGDN', label: 'SCROLL THE TABLE' }];

/** `4:07`, or a dash until a mission has been completed once. Pure. */
export function bestTime(seconds: number | null): string {
  return seconds === null || !Number.isFinite(seconds) ? '-' : mmss(seconds);
}

/** `3,400`, or a dash for a mission never attempted. Pure. */
export function bestScore(record: ArchetypeRecord): string {
  return record.attempts <= 0 ? '-' : Math.round(record.bestScore).toLocaleString('en-US');
}

export interface RecordGroup {
  group: MissionGroup;
  /** Missions of the group that have been attempted, in mission-number order. */
  flown: Array<{ info: ArchetypeInfo; record: ArchetypeRecord }>;
  /** Missions in the group, attempted or not. */
  total: number;
}

/** The table's contents: every group in picker order, each with the missions of it that have been flown. Pure. */
export function recordGroups(records: Pick<Records, 'byArchetype'>): RecordGroup[] {
  return groupedArchetypes().map(({ group, missions }) => ({
    group,
    total: missions.length,
    flown: missions.flatMap((info) => {
      const record = records.byArchetype[info.id];
      return record !== undefined && record.attempts > 0 ? [{ info, record }] : [];
    }),
  }));
}

function missionTable(ctx: Ctx, records: Records): HTMLElement {
  const p = panel('BY MISSION', 'menu-grow menu-records-table');
  // Focusable, so the keyboard can scroll a long table (Page Up / Page Down, as on the briefing).
  p.body.setAttribute('tabindex', '0');
  p.body.setAttribute('aria-label', 'RECORDS BY MISSION');
  const groups = recordGroups(records);
  const flown = groups.reduce((sum, g) => sum + g.flown.length, 0);
  const total = groups.reduce((sum, g) => sum + g.total, 0);
  const groupName = (group: MissionGroup): string => ctx.describe(`group:${group}`).name;

  if (flown === 0) {
    p.body.append(
      el('div', 'menu-records-empty menu-prose', `NO MISSION FLOWN YET. ${total} ON THE LIST.`),
      el('div', 'menu-dim menu-small menu-prose', 'OPERATION AND CLASSIC MISSIONS ARE RECORDED HERE. PRACTICE AND THE'
        + ' DAILY DROP ARE NOT.'),
    );
  } else {
    p.body.append(
      box(
        'menu-records-row menu-records-head',
        el('span', '', '#'), el('span', '', 'MISSION'),
        el('span', 'menu-records-num', 'FLOWN'), el('span', 'menu-records-num', 'DONE'),
        el('span', 'menu-records-num', 'BEST'), el('span', 'menu-records-num', 'TIME'),
      ),
    );
  }
  for (const g of groups) {
    if (g.flown.length === 0) continue;
    const head = el('div', 'menu-records-group', `${groupName(g.group)} - ${g.flown.length} OF ${g.total} FLOWN`);
    head.setAttribute('role', 'heading');
    p.body.append(head);
    for (const { info, record: r } of g.flown) {
      const d = ctx.describe(info.id);
      const row = box(
        'menu-records-row',
        el('span', '', archetypeNumber(info.id)),
        el('span', 'menu-records-name menu-paper', d.name),
        el('span', 'menu-records-num', String(r.attempts)),
        el('span', 'menu-records-num', String(r.completions)),
        el('span', 'menu-records-num', bestScore(r)),
        el('span', 'menu-records-num', bestTime(r.bestSeconds)),
      );
      row.title = d.text;
      p.body.append(row);
    }
  }
  // The rest are counted, not listed: thirty-odd rows of dashes tell nobody anything.
  const rest = groups.filter((g) => g.flown.length < g.total)
    .map((g) => `${groupName(g.group)} ${g.total - g.flown.length}`);
  if (rest.length > 0) {
    p.body.append(el('div', 'menu-records-rest menu-dim menu-small menu-prose',
      `NOT YET FLOWN: ${total - flown} OF ${total} - ${rest.join(', ')}.`));
  }
  if (flown > 0) {
    const legend = 'BEST = HIGHEST SCORE. TIME = FASTEST SUCCESSFUL MISSION, CLOCK USED.';
    p.body.append(el('div', 'menu-dim menu-small menu-prose', legend));
  }
  return p.panel;
}

function fact(label: string, value: string, who = ''): HTMLElement {
  const right = el('span', 'menu-records-who', who === '' ? value : `${who} - ${value}`);
  return box('menu-records-fact', el('span', 'menu-dim', label), right);
}

function company(records: Records): HTMLElement {
  // Whether it fills the column is the column's business now (`tables.css`): with EVERY COMPANY
  // beneath it, it takes the height of its six lines and no more.
  const p = panel('THE COMPANY');
  const serving = records.longestServing;
  const decorated = records.mostDecorated;
  p.body.append(
    serving === null
      ? fact('LONGEST SERVING', 'NOBODY YET')
      : fact('LONGEST SERVING', `${serving.missions} MISSIONS`, serving.name),
    decorated === null
      ? fact('MOST DECORATED', 'NOBODY YET')
      : fact('MOST DECORATED', `${decorated.medals} MEDALS`, decorated.name),
    fact('BEST STREAK', `${records.bestStreak} IN A ROW`),
    fact('OPERATIONS WON', String(records.operationsWon)),
    fact('RANGERS LOST', String(records.rangersLost)),
    fact('DAILY DROPS FLOWN', String(records.daily.length)),
  );
  return p.panel;
}

/** A rank index as the ladder prints it (02 §11). Pure. */
export function rankName(index: number): string {
  return RANKS[Math.min(RANKS.length - 1, Math.max(0, Math.round(index)))] ?? RANKS[0];
}

/**
 * Every company together (server/standingsApi.mjs). The server never opens a save, so these are
 * figures each campaign FILED, and they are numbers with nobody's name on them - which is why
 * there is no name in this panel and no way to ask for one.
 *
 * Each line says what its number actually is. A best belongs to one ranger somewhere; a total is
 * everybody's added up. "OPERATIONS WON 412" beside THE COMPANY's "1" would otherwise read as a
 * claim about a player, which is the one thing the wording must not do.
 */
export function everyCompany(standings: Standings): HTMLElement {
  // How many companies is in the TITLE, not a line of its own. The column holds thirteen rows of
  // facts and four ways out, and a fourteenth row pushed the bottom of this panel off the screen -
  // so the one thing that is not a fact goes where there was already room for it.
  const p = panel(`EVERY COMPANY - ${standings.saves === 1 ? '1 FILED' : `${standings.saves} FILED`}`);
  // Hover, as `hinted` does elsewhere: the assurance in full, where it costs the column nothing.
  p.panel.title = 'EACH COMPANY FILES SIX NUMBERS. NO NAME, NO CALLSIGN, NOTHING THAT SAYS WHO.';
  p.body.append(
    fact('HIGHEST RANK', `${rankName(standings.highestRank)} - BY SOMEBODY`),
    fact('MOST MISSIONS', `${standings.mostMissions} BY ONE RANGER`),
    fact('MOST DECORATED', `${standings.mostMedals} MEDALS ON ONE MAN`),
    fact('BEST STREAK', `${standings.bestStreak} IN A ROW`),
    fact('OPERATIONS WON', `${standings.operationsWon} ALL TOLD`),
    fact('RANGERS LOST', `${standings.rangersLost} ALL TOLD`),
  );
  return p.panel;
}

export function recordsScreen(ctx: Ctx): Screen {
  const f = frame('records', 'RECORDS', HINTS);
  const back = button('BACK', () => ctx.nav.title());
  const side = box('menu-col menu-records-side');
  // How many panels stand here depends on whether there is a server to ask, so the column takes
  // the slack and scrolls (`tables.css`). Sharing its height out between them instead took the
  // bottom row off each - and a fact you cannot see is worse than a screen you have to scroll.
  const panels = box('menu-col menu-grow menu-scroll');
  let records: Records | null = null;
  try {
    records = ctx.api.records();
  } catch (err) {
    const error = el('div', 'menu-error menu-prose', errorMessage(err));
    error.setAttribute('role', 'alert');
    panels.append(error);
  }
  if (records !== null) {
    f.body.append(missionTable(ctx, records));
    panels.append(company(records));
  } else {
    f.body.append(box('menu-col menu-grow'));
  }
  const links = box(
    'menu-col',
    // The panel above names the longest-serving and the most decorated; this is where you go to
    // read what either of them actually did.
    button('PERSONNEL', () => ctx.nav.personnel(), 'menu-btn-small'),
    button('HALL OF HONOR', () => ctx.nav.hall(), 'menu-btn-small'),
    button('MEMORIAL WALL', () => ctx.nav.memorial(), 'menu-btn-small'),
    button('DAILY DROP', () => ctx.nav.daily(), 'menu-btn-small'),
  );
  side.append(panels, links, back);
  f.body.append(side);
  // Every company's figures, where there is a server to ask. It must cost the player nothing: no
  // spinner that may never resolve and no error to dismiss - the panel arrives or it never appears.
  const wall = ctx.wall;
  if (wall !== undefined) {
    void (async () => {
      const standings = await wall.standings().catch(() => null);
      // Nobody has filed anything: six zeroes and "0 COMPANIES" say less than no panel at all.
      if (standings === null || standings.saves === 0) return;
      panels.append(everyCompany(standings));
    })();
  }
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => back };
}
